/**
 * Floating messengers custom element.
 * - Tap the trigger to open/close the list (same behavior on desktop & mobile).
 * - Closes on outside click or Escape.
 * - Lifts itself above the sticky "Add to cart" bar when it becomes visible.
 */
class FloatingMessengers extends HTMLElement {
  constructor() {
    super();
    this._onTriggerClick = this._onTriggerClick.bind(this);
    this._onDocumentClick = this._onDocumentClick.bind(this);
    this._onKeyDown = this._onKeyDown.bind(this);
    this._onBarMutation = this._onBarMutation.bind(this);
    this._onResize = this._onResize.bind(this);
  }

  connectedCallback() {
    this.trigger = this.querySelector('[data-fm-trigger]');
    this.list = this.querySelector('[data-fm-list]');
    if (!this.trigger || !this.list) return;

    this.trigger.addEventListener('click', this._onTriggerClick);
    document.addEventListener('click', this._onDocumentClick);
    document.addEventListener('keydown', this._onKeyDown);

    // Watch the sticky add-to-cart bar to lift ourselves above it when shown.
    this._setupStickyBarWatcher();
    window.addEventListener('resize', this._onResize, { passive: true });
  }

  disconnectedCallback() {
    if (this.trigger) this.trigger.removeEventListener('click', this._onTriggerClick);
    document.removeEventListener('click', this._onDocumentClick);
    document.removeEventListener('keydown', this._onKeyDown);
    window.removeEventListener('resize', this._onResize);
    if (this._barObserver) this._barObserver.disconnect();
    if (this._domObserver) this._domObserver.disconnect();
  }

  _onTriggerClick(e) {
    e.stopPropagation();
    this._toggle();
  }

  _onDocumentClick(e) {
    if (this.dataset.open !== 'true') return;
    if (!this.contains(e.target)) this._close();
  }

  _onKeyDown(e) {
    if (e.key === 'Escape' && this.dataset.open === 'true') this._close();
  }

  _onResize() {
    if (this.dataset.barVisible === 'true') this._applyBarOffset();
  }

  _toggle() {
    if (this.dataset.open === 'true') this._close();
    else this._open();
  }

  _open() {
    this.dataset.open = 'true';
    this.trigger.setAttribute('aria-expanded', 'true');
  }

  _close() {
    this.dataset.open = 'false';
    this.trigger.setAttribute('aria-expanded', 'false');
  }

  /* ----- Sticky add-to-cart bar coordination ----------------------------- */

  _setupStickyBarWatcher() {
    // The bar may not exist yet (e.g. lazy section). Look for it; if absent,
    // observe the body for it to appear, then start watching.
    this._bar = document.querySelector('.sticky-add-to-cart__bar');
    if (this._bar) {
      this._observeBar(this._bar);
    } else {
      this._domObserver = new MutationObserver(() => {
        const bar = document.querySelector('.sticky-add-to-cart__bar');
        if (bar) {
          this._bar = bar;
          this._domObserver.disconnect();
          this._observeBar(bar);
        }
      });
      this._domObserver.observe(document.body, { childList: true, subtree: true });
    }
  }

  _observeBar(bar) {
    // Initial state
    this._updateBarVisibility(bar.getAttribute('data-stuck') === 'true');

    this._barObserver = new MutationObserver(this._onBarMutation);
    this._barObserver.observe(bar, { attributes: true, attributeFilter: ['data-stuck'] });
  }

  _onBarMutation(mutations) {
    for (const m of mutations) {
      if (m.attributeName === 'data-stuck') {
        this._updateBarVisibility(this._bar.getAttribute('data-stuck') === 'true');
      }
    }
  }

  _updateBarVisibility(isVisible) {
    if (isVisible) {
      this.dataset.barVisible = 'true';
      this._applyBarOffset();
    } else {
      this.dataset.barVisible = 'false';
      this.style.removeProperty('--fm-bar-offset');
    }
  }

  _applyBarOffset() {
    if (!this._bar) return;
    const rect = this._bar.getBoundingClientRect();
    // Add a small gap above the bar so the button doesn't kiss it.
    const offset = Math.max(0, rect.height) + 12;
    this.style.setProperty('--fm-bar-offset', `${offset}px`);
  }
}

if (!customElements.get('floating-messengers')) {
  customElements.define('floating-messengers', FloatingMessengers);
}
